import React, { useEffect } from "react";
import PulloutDrawer from "../../../components/_maps/main/PulloutDrawer/PulloutDrawer";
import useMobileScreen from "../../../hooks/useMobileScreen";
import { useTranslation } from "react-i18next";
import { useMapPageContext } from "../../MapPage/MapPageContext";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import { CustomLoader } from "../../Loader/Loader";
import { StyledSearchResults } from "..";
import NoResultsFound from "../NoResultsFound";
import useSearchParams from "../../../hooks/useSearchParams";
import { useActions } from "../../../hooks/useActions";

interface Props {
  count: number;
  children: React.ReactNode;
  handleLoadFeed: () => void;
  onClearAllFilters: () => void;
}

const SearchResultsResponsive = ({
  handleLoadFeed,
  onClearAllFilters,
  children,
  count,
}: Props) => {
  const isMobile = useMobileScreen();
  const { searchResultsRef } = useMapPageContext();

  const selectedDataset = useSearchParams().getSearchParam("dataset");

  const { t: tDataset } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.downloadResults.datasets",
  });

  const { isLoading, isListViewActive, isPulloutDrawerOpen } = useTypedSelector(
    (state) => state.mapInterface
  );
  const { setIsListViewActive, setIsPulloutDrawerOpen } = useActions();

  const isCybersecurityMobile = isMobile && selectedDataset === "cybersecurity";
  useEffect(() => {
    if (!isMobile || isCybersecurityMobile) {
      return;
    }

    if (isListViewActive) {
      setIsListViewActive(false);
    }
  }, [isMobile, isCybersecurityMobile, isListViewActive, setIsListViewActive]);

  useEffect(() => {
    if (!isCybersecurityMobile) {
      return;
    }

    if (!isListViewActive) {
      setIsListViewActive(true);
    }

    if (!isPulloutDrawerOpen) {
      setIsPulloutDrawerOpen(true);
    }
  }, [
    isCybersecurityMobile,
    isListViewActive,
    isPulloutDrawerOpen,
    setIsListViewActive,
    setIsPulloutDrawerOpen,
  ]);

  const pulloutDrawerHeaderText =
    isLoading || count === 0 ? "" : `${count} ${tDataset(selectedDataset)}`;

  if (!isMobile && isLoading && count === 0) {
    return (
      <StyledSearchResults>
        <CustomLoader />
      </StyledSearchResults>
    );
  }
  if (!isMobile && count === 0) {
    return (
      <StyledSearchResults>
        <NoResultsFound onClearFilters={onClearAllFilters} />
      </StyledSearchResults>
    );
  }

  return (
    <StyledSearchResults>
      {isMobile ? (
        <PulloutDrawer
          constraintsContainerRef={searchResultsRef}
          header={pulloutDrawerHeaderText}
          onScrollToBottom={handleLoadFeed}
          noResults={count === 0}
          forceOpen={isCybersecurityMobile}
          isToggleDisabled={isCybersecurityMobile}
        >
          {count ? (
            children
          ) : (
            <NoResultsFound onClearFilters={onClearAllFilters} />
          )}
          {isLoading && <CustomLoader />}
        </PulloutDrawer>
      ) : (
        <>{isLoading && count === 0 ? <CustomLoader /> : children}</>
      )}
    </StyledSearchResults>
  );
};

export default SearchResultsResponsive;
